import React from "react";
import { TEXT_LEVELS, Text, plainText, safeElement, type TextLevel } from "../../atoms/text/text";
import type { LineupColumn } from "./channel-lineup.types";
import { ChannelRow } from "./channel-row";
import { GridSection } from "./grid-section";
import { IndicatorLegend } from "./indicator-legend";
import type { LineupRowCopy } from "./row-copy.shared";
import { MyFreeIntro } from "./signature-grid";
import type {
  SignatureSearchResults,
  SignatureSearchRow,
  SignatureSearchStatus
} from "./signature-grid.shared";
import { SubscriptionRow } from "./subscription-row";
import styles from "./search-results.module.css";
import { TooltipScope } from "./tooltip-scope.client";
import { withSafeRender } from "../../atoms/safe-render";

/** The Signature packages grid while a search is active — GQ1-GQ6. not A filtered `SignatureGrid`. */

export interface SearchResultsProps {
  /** Resolver-fed (camelCase). */
  columns: LineupColumn[];
  /** RESOLVER-FED (camelCase). GQ1-GQ4, already decided — the merge, the
   *  order, the legend's set and AX4's count all come from here. */
  results: SignatureSearchResults;
  /**
   * GQ6. Defaults to `"ready"`. `"partial"` draws its message and the results; `"error"` draws its
   * message instead of them.
   */
  status?: SignatureSearchStatus;
  /** GQ3's heading wording. Absent, no heading. */
  search_heading_content?: string;
  /** GQ5 — the no-match wording. */
  empty_message_content?: string;
  /** GQ6's partial wording. */
  partial_message_content?: string;
  /** GQ6's total-failure wording. */
  error_message_content?: string;
  /** Page-set. The authored wording every row draws, including GQ4's add-on note. */
  rowCopy?: LineupRowCopy;
  /** GQ3 — the legend's ✓ line, content-managed. */
  legend_included_content?: string;
  /** GQ3 — the legend's $ line, content-managed. */
  legend_addon_content?: string;
  /** GQ3 — the legend's ★ line, content-managed. */
  legend_promo_content?: string;
  /** GQ4/GS11 — the MyFree lockup above the MyFree rows. Absent, no logo. */
  myfree_logo_url?: string;
  /** GQ4/GS11's button wording. Needs a URL too, or nothing renders. */
  myfree_cta_label?: string;
  /** GQ4/GS11's button destination. Needs a label too, or nothing renders. */
  myfree_cta_url?: string;
  /** GQ4/GS11's button accessible name, as authored. Absent, the visible wording names it. */
  myfree_cta_aria_label?: string;
  /** GR5 — the legal note for an RSN's tier, keyed by tier number. Forwarded
   *  to every row untouched; absent means no legal line anywhere. */
  tierLegalNotes?: Record<number, string>;
  /** GR5 — the note an RSN falls back to when its tier has no entry. */
  rsn_legal_default_content?: string;
  /** AX6 — makes every row's channel logo a tooltip trigger. OFF by default;
   *  only the boolean `true` switches it on (see the wrapper's gate). */
  show_channel_logo_tooltip?: boolean;
  /**
   * AX5 — "page heading, then Search results, then the MyFree DIRECTV label". This is the first of
   * those two, so it defaults to `h2` under the page's `h1`; the MyFree label is drawn one level
   * below whatever is set here.
   */
  headingLevel?: TextLevel;
  /** Layout only. */
  className?: string;
}

/** AX5 — the MyFree label sits UNDER the "Search results" heading, so it
 *  follows it by one level. `h6` is the floor the scale has. */
const NEXT_LEVEL: Record<TextLevel, TextLevel> = {
  h1: "h2",
  h2: "h3",
  h3: "h4",
  h4: "h5",
  h5: "h6",
  h6: "h6",
  p: "p"
};

function SearchRows({
  rows,
  columns,
  tierLegalNotes,
  defaultLegalNote,
  showLogoTooltip,
  rowCopy
}: {
  rows: SignatureSearchRow[];
  columns: LineupColumn[];
  tierLegalNotes?: Record<number, string>;
  defaultLegalNote?: string;
  showLogoTooltip: boolean;
  rowCopy?: LineupRowCopy;
}) {
  return (
    <>
      {rows.map(row => (
        <ChannelRow
          /* GQ4 — the add-on look travels as a class rather than a prop:
             `channel-row.tsx` draws whatever cells it is handed and owns no
             membership of its own, which is the contract its header states. */
          className={row.addonStyled ? styles.addonRow : undefined}
          columns={columns}
          defaultLegalNote={defaultLegalNote}
          key={row.channel.id}
          /* GQ4's stripe said in words: the stripe is colour alone, so the two travel together or
             WCAG 1.4.1 is open. */
          note={row.addonStyled ? rowCopy?.addonNoteLabel : undefined}
          row={row}
          rowCopy={rowCopy}
          showLogoTooltip={showLogoTooltip}
          tierLegalNotes={tierLegalNotes}
        />
      ))}
    </>
  );
}

function SearchResultsImpl({
  columns,
  results,
  status = "ready",
  search_heading_content: searchHeadingContent,
  empty_message_content: emptyMessageContent,
  partial_message_content: partialMessageContent,
  error_message_content: errorMessageContent,
  legend_included_content: legendIncluded,
  legend_addon_content: legendAddon,
  legend_promo_content: legendPromo,
  myfree_logo_url: myFreeLogoUrl,
  myfree_cta_label: myFreeCtaLabel,
  myfree_cta_url: myFreeCtaUrl,
  myfree_cta_aria_label: myFreeCtaAriaLabel,
  tierLegalNotes,
  rsn_legal_default_content: rsnLegalDefault,
  show_channel_logo_tooltip: showChannelLogoTooltip = false,
  rowCopy,
  headingLevel = "h2",
  className
}: SearchResultsProps) {
  const level = safeElement(headingLevel, "h2", TEXT_LEVELS);
  const myFreeLevel = NEXT_LEVEL[level];
  const heading = plainText((searchHeadingContent ?? "").trim());
  const partialMessage = plainText((partialMessageContent ?? "").trim());
  const errorMessage = plainText((errorMessageContent ?? "").trim());
  const emptyMessage = plainText((emptyMessageContent ?? "").trim());
  const myFreeLabel = plainText(results.myFreeLabel);

  /* Same contract `signature-grid.tsx` publishes. `repeat(0, …)` is invalid
     CSS and takes the whole declaration with it, so the floor is 1. */
  const trackStyle = { "--cl-columns": Math.max(columns.length, 1) } as React.CSSProperties;

  const empty = results.total === 0;

  /* GQ6'S discriminator is what is on screen, not `status`. */
  const failed = status === "error" || (status === "partial" && empty);
  const partial = status === "partial" && !empty;

  /* GQ3 — "only the indicator types present in the results". Gated on the data as well as the
     failure arm: an empty key would still draw its wrapper and gap. */
  const legend = failed || (results.memberships ?? []).length === 0 ? null : (
    <IndicatorLegend
      addonLine={legendAddon}
      includedLine={legendIncluded}
      memberships={results.memberships}
      promoLine={legendPromo}
    />
  );

  const body = (
    <>
      {/* GQ6 — "some results still show": the message AND the results below
          it. With nothing below, `partial` is false and the arm under this one
          draws the total-failure wording instead. */}
      {partial && partialMessage !== "" && (
        <Text as="p" className={styles.message} variant="body-2">
          {partialMessage}
        </Text>
      )}

      {failed && errorMessage !== "" && (
        <Text as="p" className={styles.message} variant="body-2">
          {errorMessage}
        </Text>
      )}

      {/* GQ5 — "if nothing matches". A match on a subscription listed in the grid, or on a MyFree channel, is counted in `total`, so neither reaches this arm. */}
      {status === "ready" && empty && emptyMessage !== "" && (
        <Text as="p" className={styles.empty} variant="body-2">
          {emptyMessage}
        </Text>
      )}

      {!failed && !empty && (
        <>
          {/* GQ4, first — matching subscriptions. */}
          {results.subscriptionRows.map(row => (
            <SubscriptionRow columns={columns} key={row.subscription.id} row={row} rowCopy={rowCopy} />
          ))}

          {/* GQ4, second — matching channels. */}
          <SearchRows
            columns={columns}
            defaultLegalNote={rsnLegalDefault}
            rowCopy={rowCopy}
            rows={results.rows}
            showLogoTooltip={showChannelLogoTooltip}
            tierLegalNotes={tierLegalNotes}
          />

          {/* GQ4, third — matching MyFree channels, under the MyFree label with its logo and button. The label is drawn only when there is something under it, which is the same emptiness rule GS6 applies to a section. */}
          {results.myFreeRows.length > 0 && (
            <>
              {myFreeLabel !== "" && (
                <Text
                  as={myFreeLevel}
                  className={styles.myFreeLabel}
                  variant="subhead-2"
                  weight="medium"
                >
                  {myFreeLabel}
                </Text>
              )}
              <MyFreeIntro
                ctaAriaLabel={myFreeCtaAriaLabel}
                ctaHref={myFreeCtaUrl}
                ctaLabel={myFreeCtaLabel}
                logoUrl={myFreeLogoUrl}
              />
              <SearchRows
                columns={columns}
                defaultLegalNote={rsnLegalDefault}
                rowCopy={rowCopy}
                rows={results.myFreeRows}
                showLogoTooltip={showChannelLogoTooltip}
                tierLegalNotes={tierLegalNotes}
              />
            </>
          )}
        </>
      )}
    </>
  );

  return (
    <TooltipScope>
      <div className={[styles.results, className].filter(Boolean).join(" ")} style={trackStyle}>
        {/* GQ3/AX5 — the results open on their heading, drawn as a section bar like the grid's
            (`16562:25304`) and in the failure arm too: AX5's order is about the outline. */}
        {heading !== "" ? (
          <GridSection defaultOpen heading={heading} headingLevel={level} legendSlot={legend}>
            {/* The bar's copy is aria-hidden so it stays out of the summary's name; this copy is the
                key a screen reader reaches, once, at the top of the results (GQ3). */}
            {legend !== null && <div className="dds-sr-only">{legend}</div>}
            {body}
          </GridSection>
        ) : (
          <>
            {legend !== null && <div className={styles.legend}>{legend}</div>}
            {body}
          </>
        )}
      </div>
    </TooltipScope>
  );
}
export const SearchResults = withSafeRender(SearchResultsImpl, "SearchResults");

export default SearchResults;
